import React, { FC, useState, useCallback } from "react";
import { Fade } from "react-awesome-reveal";
import classNames from "classnames";
import { useI18next } from "gatsby-plugin-react-i18next";
import { useTranslation } from "gatsby-plugin-react-i18next";
import { BlockHeader } from "@app/components/common/block-header/BlockHeader";
import { FaqItem } from "@app/components/common/faq-group/components/faq-item/FaqItem";
import { LinkButton } from "@app/components/common";
import { useViewVariant } from "@app/hooks/use-view-variant/useViewVariant";
import { IRichText, TextType } from "@app/models/storyblok/basic";
import { DEFAULT_FADE_DURATION } from "@app/constants";
import { useFaqLibrary } from "./hooks/use-faq-library/useFaqLibrary";

type Props = {
  questions: string[];
  title?: IRichText<TextType>;
  hideAllQuestionsLink?: boolean;
};

export const FaqLibrary: FC<Props> = ({
  title,
  questions,
  hideAllQuestionsLink,
}) => {
  const { isContentOnly } = useViewVariant();
  const [activeQuestion, setActiveQuestion] = useState<string>("");
  const { language, defaultLanguage } = useI18next();
  const questionsPool = useFaqLibrary(
    language === defaultLanguage ? "default" : language,
    isContentOnly,
    questions,
  );
  const { t } = useTranslation("common");

  const onSetActiveQuestion = useCallback(
    (questionId: string) => {
      setActiveQuestion((prev) => (prev === questionId ? "" : questionId));
    },
    [setActiveQuestion],
  );

  const lastQuestionIndex = questionsPool.length - 1;

  return (
    <div
      className={classNames(
        "mt-20 flex flex-col gap-15 px-5",
        "lg:mx-auto lg:mt-25 lg:w-[64%] lg:gap-18 lg:px-0",
        "xl:mt-40 xl:w-[58%] xl:max-w-[977px] xl:gap-15",
      )}
    >
      {title ? (
        <div
          className={classNames(
            "text-large text-primary-content",
            "xl:text-xx-large xl:w-[62%]",
          )}
        >
          <Fade duration={DEFAULT_FADE_DURATION} triggerOnce>
            <BlockHeader richText={title} />
          </Fade>
        </div>
      ) : null}

      <div className={classNames("text-primary-content")}>
        {questionsPool.map((question, index) => (
          <Fade
            key={question.uuid}
            duration={DEFAULT_FADE_DURATION}
            triggerOnce
          >
            <FaqItem
              question={question.question}
              isActive={question.uuid === activeQuestion}
              onSetActive={() => onSetActiveQuestion(question.uuid)}
              isDividerHidden={index === lastQuestionIndex}
            />
          </Fade>
        ))}
        <div className={classNames("mt-5", "xxl:mt-10")}>
          {isContentOnly || hideAllQuestionsLink ? null : (
            <LinkButton label={t("see_all")} to={"/faq"} />
          )}
        </div>
      </div>
    </div>
  );
};
